import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./style.scss";
import {
  Grid,
  Box,
  Text,
  Button,
  DropButton,
  Heading,
  TextInput,
} from "grommet";
import { Search } from "grommet-icons";
import {
  Menu,
  Notification,
  Basket,
  MailOption,
  Alert,
  DocumentUser,
  Lock,
  CaretDown,
  Trash,
} from "grommet-icons";
import io from "socket.io-client";
import { NavBar } from "./";
import BillingGraceModal from "./BillingGraceModal";
import AssistantPanel from "./AssistantPanel";
import UserService from "../../services/user.service";
import AuthService from "../../services/auth.service";
import NotificationService from "../../services/notification.service";
import { API_URL, SERVER_URL, authHeader } from "../../services/api";
import {
  useSetCurrentUser,
  useFetchHotel,
  useFetchDoors,
  useFetchRoomTypes,
} from "./redux/hooks";
import { getFirebaseToken, onFirebaseMessageListener } from "@common/firebase";
import {
  presentToastInfo,
  presentToastLink,
  presentToastErrorContent,
} from "@shared/Toast";
import { useTranslation } from "react-i18next";
import aiAssistantSparkles from "../../images/ai-assistant-sparkles.svg";

export default function DashboardPage({ children }) {
  const routeContent = children || <Outlet />;
  const { hotel, fetchHotel } = useFetchHotel();
  const { doors, fetchDoors } = useFetchDoors();
  const { roomTypes, fetchRoomTypes } = useFetchRoomTypes();
  const [sidebar, setSidebar] = useState(true);
  const [assistantOpen, setAssistantOpen] = useState(
    () => window.location.pathname === "/askai",
  );
  const [assistantOverModal, setAssistantOverModal] = useState(false);
  const [assistantTriggerBounds, setAssistantTriggerBounds] = useState(null);
  const [assistantPortalTarget, setAssistantPortalTarget] = useState(
    () => document.body,
  );
  const assistantTriggerAnchorRef = useRef(null);
  const assistantTriggerButtonRef = useRef(null);
  const { currentUser, setCurrentUser, isLoxeAdmin, isAdmin } =
    useSetCurrentUser();
  const isGroupUser = currentUser?.role === "user";
  const isUser =
    currentUser?.role === "user" || currentUser?.role === "user-multi";
  const navigate = useNavigate();
  const location = useLocation();
  const [width, setWidth] = useState(window.innerWidth);
  const assistantPortalBounds =
    assistantPortalTarget === document.body
      ? { left: 0, top: 0 }
      : assistantPortalTarget.getBoundingClientRect();

  const openAssistant = useCallback(() => {
    setAssistantOverModal(
      Boolean(document.querySelector('[class*="StyledLayer__StyledOverlay"]')),
    );
    setAssistantOpen(true);
  }, []);

  useEffect(() => {
    const button = assistantTriggerButtonRef.current;
    if (!button) return undefined;
    const handleMouseDown = (event) => {
      event.stopPropagation();
      openAssistant();
    };
    button.addEventListener("mousedown", handleMouseDown);
    return () => button.removeEventListener("mousedown", handleMouseDown);
  }, [
    assistantOpen,
    assistantPortalTarget,
    assistantTriggerBounds,
    openAssistant,
  ]);

  useEffect(() => {
    const updatePortalTarget = () => {
      const overlays = document.querySelectorAll(
        '[class*="StyledLayer__StyledOverlay"]',
      );
      const activeModalLayer = overlays[overlays.length - 1]?.parentElement;
      const activeModalContainer = activeModalLayer?.querySelector(
        '[class*="StyledLayer__StyledContainer"]',
      );
      setAssistantPortalTarget(activeModalContainer || document.body);
    };
    const observer = new MutationObserver(updatePortalTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    updatePortalTarget();
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const updateAssistantTriggerBounds = () => {
      const bounds = assistantTriggerAnchorRef.current?.getBoundingClientRect();
      if (!bounds) return;
      setAssistantTriggerBounds({
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
      });
    };
    updateAssistantTriggerBounds();
    window.addEventListener("resize", updateAssistantTriggerBounds);
    return () =>
      window.removeEventListener("resize", updateAssistantTriggerBounds);
  }, [sidebar, width, hotel.notifications?.length]);

  // Redirect user/user-multi away from any /settings route
  useEffect(() => {
    if (isUser && window.location.pathname.startsWith("/settings")) {
      navigate("/guests", { replace: true });
    }
  }, [isUser, navigate]);

  useEffect(() => {
    if (location.pathname === "/askai") {
      setAssistantOpen(true);
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  const [openNotifications, setOpenNotifications] = useState(false);
  const [openHotelSelector, setOpenHotelSelector] = useState(false);
  const [hotelSearch, setHotelSearch] = useState("");
  const [pushId, setPushId] = useState();
  const { t } = useTranslation();
  const hasPasscodes = hotel.usePasscodes || hotel.lockType === "oracode";
  const isTtlock = hotel.lockType === "ttlock";
  const hotelSocketRef = useRef(null);
  const bellAudioRef = useRef(null);

  // Play bell sound for in-screen notifications
  const playBell = useCallback(() => {
    try {
      if (!bellAudioRef.current) {
        bellAudioRef.current = new Audio("/sounds/bell.mp3");
      }
      bellAudioRef.current.currentTime = 0;
      bellAudioRef.current.play().catch(() => {});
    } catch (e) {
      // Audio play may fail if user hasn't interacted with page yet
    }
  }, []);

  // Global hotel-level socket: receives notifications for ALL bookings from any page
  useEffect(() => {
    if (!hotel._id) return;

    const socket = io(SERVER_URL, {
      auth: (done) => done({ protocol: 2, headers: authHeader() }),
      transports: ["websocket"],
    });
    hotelSocketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("joinHotel", { hotelId: hotel._id }, (result) => {
        if (!result?.ok || hotelSocketRef.current !== socket || !socket.connected)
          return;
        window.dispatchEvent(
          new CustomEvent("loxe:schlage-passcode", {
            detail: { hotelId: hotel._id, refreshAll: true },
          }),
        );
      });
    });

    socket.on("hotelNotification", (payload) => {
      // Play bell sound
      playBell();

      // Show toast with link to the guest conversation
      const url = payload.url || "/";
      const title = payload.title || "New notification";
      presentToastLink(`${title} [View]`, url);

      // Refresh hotel data to update notification badge count
      fetchHotel();
    });

    socket.on("schlagePasscodeChanged", (payload) => {
      if (String(payload?.hotelId) !== String(hotel._id)) return;
      window.dispatchEvent(
        new CustomEvent("loxe:schlage-passcode", { detail: payload }),
      );
    });

    socket.on("receiveMessage", (message) => {
      window.dispatchEvent(
        new CustomEvent("loxe:hotel-message", { detail: message }),
      );
    });

    return () => {
      socket.disconnect();
      hotelSocketRef.current = null;
    };
  }, [hotel._id, fetchHotel, playBell]);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
    if (window.innerWidth < 500) setSidebar(false);
  };

  useEffect(() => {
    setSidebar(width > 500);
    window.addEventListener("resize", handleWindowSizeChange);
    getFirebaseToken(setPushId);

    onFirebaseMessageListener()
      .then((payload) => {
        fetchHotel();
        presentToastInfo(payload.notification.title);
      })
      .catch((err) => console.log("failed: ", err));

    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [fetchHotel, width]);

  const handleClearAllNotifications = async () => {
    try {
      await NotificationService.clearAll();
      setOpenNotifications(false);
      fetchHotel();
    } catch (err) {
      console.error("Failed to clear notifications", err);
    }
  };

  const DropNotifications = () => (
    <Box
      pad="small"
      background="white"
      elevation="medium"
      style={{ maxHeight: "70vh", overflow: "auto" }}
    >
      <Box
        direction="row"
        justify="between"
        align="center"
        margin={{ bottom: "medium", top: "medium" }}
      >
        <Heading>
          {t("dashboard.notifications", { count: hotel.notifications.length })}
        </Heading>
        <Button
          icon={<Trash size="small" />}
          label={t("dashboard.clearAll")}
          onClick={handleClearAllNotifications}
          plain
          style={{ color: "#FF4040", fontSize: 13, padding: "4px 10px" }}
          hoverIndicator="light-1"
        />
      </Box>
      {hotel.notifications.map((notif) => (
        <Button
          hoverIndicator="light-1"
          onClick={() => {
            window.location = notif.url;
          }}
          key={notif._id}
          className="notif"
        >
          <Box direction="row" align="center" gap="medium">
            {renderNotifIcon(notif.type)}
            <Box>
              <Text weight="bold">{notif.message}</Text>
              {notif.metaData && notif.metaData.serviceName && (
                <Text color="dark-6" style={{ fontStyle: "italic" }}>
                  {notif.metaData.serviceName}
                </Text>
              )}
              {notif.metaData && notif.metaData.message && (
                <Text color="dark-6" style={{ fontStyle: "italic" }}>
                  {notif.metaData.message}
                </Text>
              )}
              <Box justify="between" direction="row" style={{ minWidth: 250 }}>
                {!notif.metaData.roomNumber ? (
                  <Text style={{ marginRight: 30 }}>
                    {notif.metaData.guestName}
                  </Text>
                ) : (
                  <Text>
                    {notif.metaData.roomNumber &&
                      t("dashboard.room", {
                        number: notif.metaData.roomNumber,
                      })}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </Button>
      ))}
    </Box>
  );

  const renderNotifIcon = (type) => {
    switch (type) {
      case "service-new":
        return <Basket color="status-ok" />;
      case "service-cancel":
        return <Alert color="status-error" />;
      case "message":
        return <MailOption color="neutral-3" />;
      case "checkin":
        return <DocumentUser color="accent-4" />;
      case "keys-request":
        return <Lock color="neutral-4" />;
      default:
        return <Notification />;
    }
  };

  const handleHotelChange = async (hotelId) => {
    try {
      await AuthService.selectProperty(hotelId);
      setOpenHotelSelector(false);
      window.location.reload(); // Reload to fetch new hotel data
    } catch (error) {
      presentToastErrorContent(error);
    }
  };

  const showSearch = hotel.group && hotel.group.length > 3;
  const filteredHotels = hotel.group
    ? hotel.group.filter((h) =>
        h.name.toLowerCase().includes(hotelSearch.toLowerCase()),
      )
    : [];

  // SuperAdmin: sort demo first, disabled last
  const sortedHotels = isLoxeAdmin
    ? [...filteredHotels].sort((a, b) => {
        const aOrder = a.disabled ? 2 : a.demo ? 0 : 1;
        const bOrder = b.disabled ? 2 : b.demo ? 0 : 1;
        return aOrder - bOrder || a.name.localeCompare(b.name);
      })
    : filteredHotels;

  const dropHotelSelector = (
    <Box pad="medium" style={{ maxHeight: "400px", overflow: "auto" }}>
      <Heading level={3} margin={{ top: "none", bottom: "small" }}>
        {t("dashboard.selectHotel")}
      </Heading>
      {showSearch && (
        <Box margin={{ bottom: "large" }}>
          <TextInput
            placeholder={t("dashboard.searchHotels")}
            value={hotelSearch}
            onChange={(e) => setHotelSearch(e.target.value)}
            icon={<Search size="small" />}
          />
        </Box>
      )}
      {sortedHotels.map((hotelItem) => (
        <Button
          key={hotelItem._id}
          hoverIndicator="light-1"
          onClick={() => handleHotelChange(hotelItem._id)}
          style={{ padding: "10px" }}
        >
          <Box direction="row" align="center" gap="xsmall">
            <Text
              weight={
                hotelItem._id === hotel._id
                  ? "bold"
                  : isLoxeAdmin && hotelItem.demo
                    ? "bold"
                    : "normal"
              }
              style={{
                ...(isLoxeAdmin && hotelItem.disabled
                  ? { textDecoration: "line-through", opacity: 0.5 }
                  : {}),
                ...(isLoxeAdmin && hotelItem.demo ? { color: "#FFAA15" } : {}),
              }}
            >
              {hotelItem.name}
              {hotelItem._id === hotel._id && " ✓"}
            </Text>
            {isLoxeAdmin && hotelItem.lockType === "ttlock" && (
              <Text
                size="xsmall"
                weight="bold"
                color="white"
                style={{
                  background: "#4A90D9",
                  borderRadius: "4px",
                  padding: "1px 5px",
                  fontSize: "10px",
                }}
              >
                TTLock
              </Text>
            )}
            {isLoxeAdmin && hotelItem.lockType === "oracode" && (
              <Text
                size="xsmall"
                weight="bold"
                color="white"
                style={{
                  background: "#E07C24",
                  borderRadius: "4px",
                  padding: "1px 5px",
                  fontSize: "10px",
                }}
              >
                Oracode
              </Text>
            )}
            {isLoxeAdmin && hotelItem.lockType === "schlage" && (
              <Text
                size="xsmall"
                weight="bold"
                color="white"
                style={{
                  background: "#287568",
                  borderRadius: "4px",
                  padding: "1px 5px",
                  fontSize: "10px",
                }}
              >
                Schlage
              </Text>
            )}
          </Box>
        </Button>
      ))}
    </Box>
  );

  useEffect(() => {
    UserService.getUser().then(
      (response) => {
        setCurrentUser(response.data);
        fetchHotel();
        fetchDoors();
        fetchRoomTypes();
      },
      (error) => {
        window.location.href = "/sign-in";
      },
    );
  }, [fetchHotel, fetchDoors, fetchRoomTypes, setCurrentUser]);

  useEffect(() => {
    if (
      pushId &&
      currentUser &&
      (!currentUser.pushIds || !currentUser.pushIds.includes(pushId))
    ) {
      console.log("Send pushId to server", pushId);
      UserService.addPushId(pushId);
    }
  }, [pushId, currentUser]);

  if (window.location.href.indexOf("/pdf/") > 0)
    return <Box margin="large">{routeContent}</Box>;
  else
    return (
      currentUser && (
        <Grid
          fill
          rows={["auto", "flex"]}
          columns={["auto", "flex"]}
          areas={[
            { name: "header", start: [0, 0], end: [1, 0] },
            { name: "sidebar", start: [0, 1], end: [0, 1] },
            { name: "main", start: [1, 1], end: [1, 1] },
          ]}
        >
          <Box
            gridArea="header"
            className="dashboard-header"
            direction="row"
            align="center"
            justify="between"
            pad={{ horizontal: "medium", vertical: "small" }}
            background="brand"
          >
            <Button onClick={() => setSidebar(!sidebar)}>
              {!sidebar && <Menu size="14px" style={{ marginRight: 10 }} />}
              <Text size="large">
                {sidebar ? "Close menu" : "Loxe FrontDesk"}
              </Text>
              <Text
                className="dashboard-development-label"
                size="small"
                margin="medium"
              >
                {API_URL === "http://localhost:4444/v1/" && "DEVELOPMENT"}
              </Text>
            </Button>
            <Box direction="row" gap="small" align="center">
              <span
                ref={assistantTriggerAnchorRef}
                className="assistant-trigger-anchor"
                aria-hidden="true"
                style={{
                  display: "block",
                  flex: "0 0 auto",
                  width: 170,
                  height: 44,
                }}
              />
              {assistantTriggerBounds &&
                !assistantOpen &&
                createPortal(
                  <Button
                    ref={assistantTriggerButtonRef}
                    plain
                    className="assistant-trigger"
                    title={t("aiAssistant.title")}
                    onClick={(event) => event.preventDefault()}
                    style={{
                      position:
                        assistantPortalTarget === document.body
                          ? "fixed"
                          : "absolute",
                      zIndex: 100,
                      left:
                        assistantTriggerBounds.left -
                        assistantPortalBounds.left,
                      top:
                        assistantTriggerBounds.top - assistantPortalBounds.top,
                      width: assistantTriggerBounds.width,
                      height: assistantTriggerBounds.height,
                    }}
                  >
                    <span
                      className="assistant-trigger__icon"
                      aria-hidden="true"
                    >
                      <img src={aiAssistantSparkles} alt="" />
                    </span>
                    <span className="assistant-trigger__label">
                      {t("aiAssistant.title")}
                    </span>
                  </Button>,
                  assistantPortalTarget,
                )}
              {hotel.notifications && hotel.notifications.length ? (
                <Box
                  round
                  border={{ color: "status-error", size: "1px" }}
                  background="status-error"
                  pad="5px"
                >
                  <DropButton
                    style={{
                      padding: 0,
                      paddingTop: 2,
                      border: 0,
                      width: 22,
                      height: 22,
                    }}
                    label={<Notification size="16px" />}
                    open={openNotifications}
                    onOpen={() => setOpenNotifications(true)}
                    onClose={() => setOpenNotifications(false)}
                    dropContent={<DropNotifications />}
                    dropProps={{
                      align: { top: "bottom" },
                      elevation: "medium",
                      className: "notifs",
                    }}
                  />
                </Box>
              ) : (
                <Box round border={{ color: "white", size: "1px" }} pad="5px">
                  <Notification size="16px" />
                </Box>
              )}
              {(currentUser.role === "admin" || isGroupUser) &&
              hotel.group &&
              hotel.group.length > 1 ? (
                <Box
                  direction="row"
                  align="center"
                  gap="xsmall"
                  pad={{ horizontal: "small", vertical: "xsmall" }}
                  background="rgba(255, 255, 255, 0.1)"
                  round="small"
                  style={{ border: "1px solid rgba(255, 255, 255, 0.3)" }}
                >
                  <DropButton
                    plain
                    label={
                      <Box direction="row" align="center" gap="xsmall">
                        <Text weight="bold">{currentUser.hotel}</Text>
                        <CaretDown size="small" />
                      </Box>
                    }
                    open={openHotelSelector}
                    onOpen={() => setOpenHotelSelector(true)}
                    onClose={() => {
                      setOpenHotelSelector(false);
                      setHotelSearch("");
                    }}
                    dropContent={dropHotelSelector}
                    dropProps={{
                      align: { top: "bottom", right: "right" },
                      elevation: "medium",
                      round: "small",
                      background: "white",
                      border: { color: "status-unknown" },
                    }}
                  />
                </Box>
              ) : (
                <Text>{currentUser.username}</Text>
              )}
            </Box>
          </Box>
          {sidebar && (
            <NavBar
              className="no-print"
              gridArea="sidebar"
              isLoxeAdmin={isLoxeAdmin}
              isAdmin={isAdmin}
              isUser={isUser}
              hasPasscodes={hasPasscodes}
              isTtlock={isTtlock}
              houseCleaningEnabled={hotel.houseCleaningEnabled}
              aiEnabled={hotel.aiEnabled}
              whiteLabeled={hotel.whiteLabeled}
              onOpenAssistant={openAssistant}
            />
          )}
          <Box
            gridArea="main"
            justify="start"
            align="center"
            overflow="auto"
            pad="large"
            background="white"
          >
            {hotel && doors && roomTypes && routeContent}
          </Box>
          <BillingGraceModal />
          <AssistantPanel
            open={assistantOpen}
            overModal={assistantOverModal}
            portalTarget={assistantPortalTarget}
            portalOffset={assistantPortalBounds}
            onClose={() => {
              setAssistantOpen(false);
              setAssistantOverModal(false);
            }}
            onNavigate={(path) => {
              navigate(path);
              setAssistantOpen(true);
            }}
          />
        </Grid>
      )
    );
}
